import logger from '@/utils/logger';

import { Group } from '@mantine/core';
import { type CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { useMutation } from '@tanstack/react-query';
import { defined } from '@teo/types';

import { useAuth } from '../contexts/AuthContext';
import { storeAuthTokens } from '../lib/authSession';
import type { DataRefresh } from '../lib/dataRefresh.js';
import { trpc } from '../lib/trpc';
import { type SignupErrorInfo, getSignupErrorCode } from '../pages/signupErrorPresentation';

interface GoogleAuthButtonProps {
  mode: 'login' | 'signup';
  onSuccess?: DataRefresh;
  onError?: (error: SignupErrorInfo) => void;
  fullWidth?: boolean;
}

/**
 * Google Authentication Button component
 * Intent: Provide Google OAuth authentication for both login and signup flows
 */
export function GoogleAuthButton({
  mode,
  onSuccess,
  onError,
  fullWidth = false,
}: GoogleAuthButtonProps) {
  const { setUser } = useAuth();

  // Use the login mutation from TRPC
  const loginMutation = useMutation(
    trpc.auth.googleLogin.mutationOptions({
      onSuccess: async (data) => {
        storeAuthTokens(
          {
            token: data.token,
            ...defined({ refreshToken: data.refreshToken }),
          },
          'google-login-success',
        );
        setUser(data.user);
        await onSuccess?.();
      },
      onError: (error) => {
        const signupErrorCode = getSignupErrorCode(error.data);

        logger.error(
          {
            errorMessage: error.message,
            errorCode: error.data?.code,
            ...defined({ signupErrorCode }),
            errorShape: error.shape,
          },
          'Google login failed',
        );
        onError?.({
          message: error.message || 'Google login failed',
          ...defined({ errorCode: error.data?.code, signupErrorCode }),
        });
      },
    }),
  );

  // Use the signup mutation from TRPC
  const signupMutation = useMutation(
    trpc.auth.googleSignup.mutationOptions({
      onSuccess: async (data) => {
        logger.log(data, ' Google signup successful');
        storeAuthTokens(
          {
            token: data.token,
            ...defined({ refreshToken: data.refreshToken }),
          },
          'google-signup-success',
        );
        setUser(data.user);
        await onSuccess?.();
      },
      onError: (error) => {
        const signupErrorCode = getSignupErrorCode(error.data);

        logger.error(
          {
            errorMessage: error.message,
            errorCode: error.data?.code,
            ...defined({ signupErrorCode }),
            errorShape: error.shape,
          },
          'Google signup failed',
        );
        onError?.({
          message: error.message || 'Google signup failed',
          ...defined({ errorCode: error.data?.code, signupErrorCode }),
        });
      },
    }),
  );

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      logger.error('Google auth error: No credential received');
      onError?.({ message: 'No credential received from Google' });
      return;
    }

    if (mode === 'signup') {
      const inviteCode =
        new URLSearchParams(window.location.search).get('inviteCode')?.trim().toUpperCase() ?? '';

      logger.log({ inviteCode }, 'Google signup - Invite code from URL');

      if (!inviteCode) {
        logger.error('Google signup error: Missing invite code');
        onError?.({
          message:
            'Invite code is required for signup. Please ensure you have a valid invite link.',
          signupErrorCode: 'invite_code_invalid',
        });
        return;
      }

      if (inviteCode.length !== 8) {
        logger.error(
          { inviteCodeLength: inviteCode.length },
          'Google signup error: Invalid invite code length',
        );
        onError?.({
          message:
            'Invite code must be exactly 8 characters. Please ensure you have a valid invite link.',
          signupErrorCode: 'invite_code_invalid',
        });
        return;
      }

      logger.log(inviteCode, 'Attempting Google signup with invite code:');
      signupMutation.mutate({
        credential: credentialResponse.credential,
        inviteCode,
      });
    } else {
      // For login mode, no invite code is needed
      loginMutation.mutate({
        credential: credentialResponse.credential,
      });
    }
  };

  const handleGoogleError = () => {
    const error = { message: `Google ${mode} authentication failed` };
    logger.error({ error });
    onError?.(error);
  };

  return (
    <Group justify="center" mt="md" w={fullWidth ? '100%' : 'auto'}>
      <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        useOneTap
        theme="outline"
        shape="rectangular"
        text={mode === 'login' ? 'signin_with' : 'signup_with'}
        locale="en"
        containerProps={{
          style: {
            display: 'flex',
            width: fullWidth ? '100%' : 'auto',
            justifyContent: 'center',
          },
        }}
      />
    </Group>
  );
}
